import React, { useEffect, useRef, useState, useMemo } from "react";
import {
  Row, Col, Table, Spin, Select, DatePicker,
  Form, message, Modal,
} from "antd";
import { EditOutlined, CloseOutlined } from "@ant-design/icons";
import moment from "moment";
import styled from "@emotion/styled";
import { connect } from "react-redux";
import {
  getUserRoleId, getSelectedDepartmentId, getStatusListFromID,
  getUserInfo, getLeaveCategoryList, getClientid, getUserList, getRoleList,
} from "redux/selectors";
import { bindActionCreators } from "redux";
import { toggleProcessingModal } from "redux/actions";
import { CALL_API } from "common/API";
import { MONTH_LIST, APPROVAL_FILTER_TYPES, STATUS_CODE } from "common/Constants";
import {
  addDaysinDate, dateInDetail, getMonthDateRange, getKenyanDateTime,
  addMonthinDate, getMySqlDate, momentDate, getinitialdate,
} from "utils/Date";
import COLORS from "common/Colors";
import {
  includes, reduce, concat, orderBy,
} from "lodash";
import ViewMore from "organisms/ViewMore";
// ── NEW: same edit/view popup used inside the calendar flow ──
import LeaveForm from "organisms/LeaveForm";
import TimeReport from "./TimeReport";
import ClaimsReport from "./ClaimsReport";
import FundReport from "./FundReport";
import TabsList from "molecules/TabsList";
import { useLocation } from "react-router-dom";
// ── NEW: shared filled button used elsewhere (e.g. TimeReport) ──
import { ButtonFilled, ButtonOutlined } from "reusableComponent/UIButtons";

/* ─────────────── Shared style constants ─────────────── */

// ── NEW: matches the edit-icon treatment used in TimeReport.js ──
const EDIT_ICON_STYLE = {
  color: "#00685F",
  cursor: "pointer",
  background: "#deefee",
  padding: "2px",
  borderRadius: "4px",
  height: "25px",
  width: "25px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 18,
};

/* ─────────────── Styled Components ─────────────── */

const PageWrapper = styled.div``;

const PageTitle = styled.h2`
  font-size: 20px;
  font-weight: 700;
  color: #111827;
  margin: 0 0 20px 0;
  letter-spacing: -0.3px;
`;

const LeaveToolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 12px;
`;

const LeaveFilters = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const ExportBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 36px;
  padding: 0 18px;
  border-radius: 6px;
  color: #000000 !important;
  border-color: #000000 !important;
  background: #fff;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  &:hover { border-color: #000000; }
`;

const StyledTable = styled(Table)`
  padding-top: 20px;
  table {
    width: 100% !important;
  }
  .ant-table-content {
    overflow-x: auto;
    white-space: nowrap;
  }
  .ant-table-cell {
    padding: 20px;
  }
  .ant-table-thead .ant-table-cell {
    font-weight: bold;
    color: ${COLORS.SECONDARY_BLACK};
    white-space: nowrap;
  }
  .non-white {
    background: #f9f9f9;
  }
  .normal-column {
    min-width: 200px;
    white-space: normal;
  }
  /* NEW: rows are now clickable to open the view/edit popup */
  .ant-table-tbody > tr {
    cursor: pointer;
  }
`;

/* ─────────────── View Popup (matches provided screenshot) ─────────────── */

const ViewPopupHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 22px;
`;

const ViewPopupIconGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  flex-shrink: 0;
`;

const StatusDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${(props) => props.color};
  flex-shrink: 0;
`;

const ViewPopupName = styled.div`
  flex: 1;
  font-size: 17px;
  font-weight: 700;
  color: #111827;
`;

// ── kept for the close (X) icon only; edit icon now uses EDIT_ICON_STYLE ──
const EditIconBtn = styled.span`
  cursor: pointer;
  font-size: 16px;
  color: #6b7280;
  &:hover { color: #111827; }
`;

const ViewPopupRow = styled.div`
  display: flex;
  padding: 8px 0;
`;

const ViewPopupLabel = styled.div`
  width: 110px;
  flex-shrink: 0;
  font-size: 14px;
  font-weight: 600;
  color: #111827;
`;

const ViewPopupValue = styled.div`
  font-size: 14px;
  color: #374151;
`;

const ViewPopupFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
  gap: 12px;
`;

/* ─────────────── CSV Helper ─────────────── */

const downloadCSV = (rows, statusId) => {
  const baseHeaders = [
    "Leave Type", "Name", "Start Date", "Start Day",
    "End Date", "End Day", "Days",
  ];

  let headers;
  if (statusId === "5") headers = [...baseHeaders, "Approved by", "Description"];
  else if (statusId === "6") headers = [...baseHeaders, "Rejected by", "Decline Reason"];
  else headers = [...baseHeaders, "Description"];

  const escapeCell = (val) => {
    const str = val === null || val === undefined ? "" : String(val);
    return str.includes(",") || str.includes('"') || str.includes("\n")
      ? `"${str.replace(/"/g, '""')}"`
      : str;
  };

  const csvContent = [
    headers.join(","),
    ...rows.map((row) => headers.map((h) => escapeCell(row[h])).join(",")),
  ].join("\n");

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", "My Leave Report.csv");
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/* ─────────────── Component ─────────────── */

function Myleavereport({
  userRoleId, statusListFromID, selectedDepartmentId,
  userInfo, leaveCategoryList, ClientId, userList, roleList,
}) {
  // ── Read location state so returning from MarkAsPaid lands on Fund Request tab ──
  const location = useLocation();

  const [leaveList, setLeaveList] = useState([]);
  const [allLeaveList, setAllLeaveList] = useState([]);
  const [displayTaskLoader, setDisplayTaskLoader] = useState(false);
  const [leaveStatusId, setLeaveStatusId] = useState("0");
  const [CSVloader, setCSVLoader] = useState(false);

  // Initialise tabId from location.state so history.push("/myleavereport", { activeTab: "fund_approvals" }) works
  const [tabId, setTabId] = useState(
    location.state?.activeTab || "leave_approvals"
  );

  const [showCategories, setShowCategories] = useState(false);
  const [markAsPaidRecord, setMarkAsPaidRecord] = useState(null);
  const [sendRecord, setSendRecord] = useState(null);

  // ── NEW: edit popup state — same LeaveForm used by the calendar ──
  const [displayLeaveModal, toggleLeaveModal] = useState(false);
  const [leaveDetails, setLeaveDetails] = useState({});

  // ── NEW: view popup state (row click) — matches the provided screenshot ──
  const [viewPopupVisible, setViewPopupVisible] = useState(false);
  const [viewPopupRecord, setViewPopupRecord] = useState(null);

  // ── NEW: custom "Cancel Leave" confirmation modal state (Yes = ButtonFilled, No = ButtonOutlined) ──
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [recordPendingCancel, setRecordPendingCancel] = useState(null);
  const [cancelingLeave, setCancelingLeave] = useState(false);

  const isSubPageOpen = showCategories || !!markAsPaidRecord || !!sendRecord;

  const [form] = Form.useForm();
  const containerRef = useRef(null);

  /* ── derived filtered lists ── */
  const data1 = leaveList?.filter(
    (val) =>
      val?.status_id === "4" ||
      (val?.status_id === "5" && val?.having_cancel_request === "1")
  );

  const data2 = leaveList?.filter(
    (val) =>
      val?.status_id === "5" &&
      (val?.having_cancel_request === "0" ||
        val?.having_cancel_request === "" ||
        !val?.having_cancel_request)
  );

  const availableLeaveCategories = allLeaveList?.reduce((acc, item) => {
    if (item.leave_category_id && !acc.some((a) => a.leave_category_id === item.leave_category_id)) {
      acc.push({
        leave_category_id: item.leave_category_id,
        leave_category_name: item.leave_category_name,
      });
    }
    return acc;
  }, []);

  // ── NEW: opens the existing LeaveForm edit popup (same one used by the calendar) ──
  const openEditPopup = (record) => {
    setLeaveDetails(record);
    toggleLeaveModal(true);
  };

  // ── NEW: opens the new View popup (row click) ──
  const openViewPopup = (record) => {
    setViewPopupRecord(record);
    setViewPopupVisible(true);
  };

  // ── NEW: status → dot color / label, matching the screenshot.
  // ASSUMPTION: 4 = pending, 5 = approved, 6 = rejected — adjust if your
  // STATUS_CODE / statusListFromID uses different values or colors. ──
  const STATUS_COLOR_MAP = {
    4: COLORS.PENDING_LEAVE || "#faad14",
    5: COLORS.APPROVED_LEAVE || "#52c41a",
    6: COLORS.REJECTED_LEAVE || "#ff4d4f",
  };
  const STATUS_LABEL_FALLBACK = {
    4: "Waiting For Approval",
    5: "Approved",
    6: "Rejected",
  };
  const getStatusColor = (statusId) => STATUS_COLOR_MAP[statusId] || "#8c8c8c";
  const getStatusLabel = (statusId) => {
    const match = statusListFromID?.find((s) => s.status_id === statusId);
    return match?.status_name || STATUS_LABEL_FALLBACK[statusId] || "-";
  };

  // ── NEW: same "Days" resolution logic used in the table column ──
  const getDaysValue = (record) => {
    const n = record?.new_number_of_days;
    return (n !== null && n !== undefined && n !== "" && n !== "null" && n !== "undefined")
      ? n
      : record?.number_of_days || "-";
  };

  // ── NEW: only pending, or approved-without-an-existing-cancel-request, can be cancelled
  // OR amended. Mirrors the guard LeaveCalendar.js uses before it lets you touch a leave
  // that already has a cancel/amend request in flight. ──
  const isCancelable = (record) =>
    record?.status_id === "4" ||
    (record?.status_id === "5" &&
      (!record?.having_cancel_request ||
        record?.having_cancel_request === "0" ||
        record?.having_cancel_request === ""));

  // ── UPDATED: cancel action now opens a custom confirmation modal (instead of
  // Modal.confirm) so the Yes/No buttons can use ButtonFilled/ButtonOutlined. ──
  const handleCancelLeave = (record) => {
    setRecordPendingCancel(record);
    setCancelConfirmOpen(true);
  };

  // ── NEW: actually performs the cancellation once the user confirms "Yes".
  // Mirrors LeaveCalendar.js's cancelLeaveRequest/cancelLeave, hitting the
  // same approve-reject-cancel-leave endpoint. ──
  const confirmCancelLeave = async () => {
    const record = recordPendingCancel;
    if (!record) return;
    try {
      setCancelingLeave(true);
      const { code } = await CALL_API(
        `approve-reject-cancel-leave/${ClientId}`,
        "post",
        {
          user_leave_id: record.user_leave_id,
          status_id: "9",
          is_cancel_request: false,
          start_date: getMySqlDate(record.start_date),
          end_date: getMySqlDate(record.end_date),
          leave_applied_userid: record.userid,
        }
      );
      if (code === STATUS_CODE.SUCCESS) {
        message.success("Your leave application has been cancelled.");
        setViewPopupVisible(false);
        getLeaveList();
      } else if (code === STATUS_CODE.SOMETHING_WENT_WRONG) {
        message.error("Oops!! something went wrong.");
      } else if (code === STATUS_CODE.INVALID_PAYLOAD) {
        message.error("Invalid payload. Please enter correct data.");
      } else if (code === STATUS_CODE.RECORD_EXIST) {
        message.error("Leave already exist.");
      }
    } catch (err) {
      console.error("[confirmCancelLeave] Error -->", err);
    } finally {
      setCancelingLeave(false);
      setCancelConfirmOpen(false);
      setRecordPendingCancel(null);
    }
  };

  // ── NEW: resolves approved_rejected_by (which the API returns as the user's
  // EMAIL, not their userid) into a display name by matching against userList.
  // Falls back through userid/employeeid too, in case some records use those. ──
  const getApproverName = (approvedRejectedBy) => {
    if (!approvedRejectedBy) return "-";
    const user = userList?.find(
      (val) =>
        val?.email === approvedRejectedBy ||
        val?.userid === approvedRejectedBy ||
        val?.employeeid === approvedRejectedBy
    );
    return user?.fullname || approvedRejectedBy || "-";
  };

  /* ── columns ── */
  const columns = [
    {
      title: "Leave Type",
      dataIndex: "leave_category_name",
      key: "leave_category_name",
      width: "10%",
    },
    {
      title: "Name",
      dataIndex: "fullname",
      key: "fullname",
      width: "10%",
    },
    {
      title: "Start Date",
      dataIndex: "start_date",
      key: "start_date",
      width: "10%",
      render: (_, record) => {
        const d = !record.new_start_date || record.new_start_date === "null" || record.new_start_date === ""
          ? record.start_date : record.new_start_date;
        return moment(d).format("DD MMM YYYY");
      },
      sorter: (a, b) => {
        const dA = (a.new_start_date === "null" || a.new_start_date === "" || !a.new_start_date)
          ? moment(a.start_date) : moment(a.new_start_date);
        const dB = (b.new_start_date === "null" || b.new_start_date === "" || !b.new_start_date)
          ? moment(b.start_date) : moment(b.new_start_date);
        return dA - dB;
      },
    },
    {
      title: "Start Day",
      dataIndex: "start_date",
      key: "start_day",
      width: "10%",
      render: (_, record) => {
        const d = !record.new_start_date || record.new_start_date === "null" || record.new_start_date === ""
          ? record.start_date : record.new_start_date;
        return moment(d).format("dddd");
      },
    },
    {
      title: "End Date",
      dataIndex: "end_date",
      key: "end_date",
      width: "10%",
      render: (_, record) => {
        const d = !record.new_end_date || record.new_end_date === "null" || record.new_end_date === ""
          ? record.end_date : record.new_end_date;
        return moment(d).format("DD MMM YYYY");
      },
      sorter: (a, b) => {
        const dA = (a.new_end_date === "null" || a.new_end_date === "" || !a.new_end_date)
          ? moment(a.end_date) : moment(a.new_end_date);
        const dB = (b.new_end_date === "null" || b.new_end_date === "" || !b.new_end_date)
          ? moment(b.end_date) : moment(b.new_end_date);
        return dA - dB;
      },
    },
    {
      title: "End Day",
      dataIndex: "end_date",
      key: "end_day",
      width: "10%",
      render: (_, record) => {
        const d = !record.new_end_date || record.new_end_date === "null" || record.new_end_date === ""
          ? record.end_date : record.new_end_date;
        return moment(d).format("dddd");
      },
    },
    {
      title: "Days",
      dataIndex: "number_of_days",
      key: "number_of_days",
      width: "10%",
      render: (_, record) => {
        const n = record?.new_number_of_days;
        return (n !== null && n !== undefined && n !== "" && n !== "null" && n !== "undefined")
          ? n
          : record?.number_of_days || "-";
      },
    },
  ];

  if (leaveStatusId === "5") {
    columns.push({
      title: "Approved by",
      dataIndex: "approved_rejected_by",
      key: "approved_rejected_by",
      width: "10%",
      render: (value) => getApproverName(value),
    });
  }

  if (leaveStatusId === "6") {
    columns.push(
      {
        title: "Rejected by",
        dataIndex: "approved_rejected_by",
        key: "rejected_by",
        width: "10%",
        render: (value) => getApproverName(value),
      },
      {
        title: "Decline Reason",
        dataIndex: "decline_reason",
        key: "decline_reason",
        width: "10%",
        className: "normal-column",
        render: (reason) => <ViewMore value={reason} />,
      }
    );
  } else {
    columns.push({
      title: "Description",
      dataIndex: "reason",
      key: "reason",
      width: "30%",
      className: "normal-column",
      render: (reason) => <ViewMore value={reason} />,
    });
  }

  // ── UPDATED: Actions column — mirrors LeaveCalendar.js's amend flow. Pending
  // leaves keep their direct edit affordance, and approved leaves (that don't
  // already have a cancel/amend request in flight) now also show the edit icon
  // so they can be amended, instead of only being cancellable from the view popup. ──
  columns.push({
    title: "Actions",
    key: "actions",
    width: "8%",
    render: (_, record) =>
      isCancelable(record) ? (
        <EditOutlined
          style={EDIT_ICON_STYLE}
          onClick={(e) => {
            e.stopPropagation(); // don't also trigger the row's view popup
            openEditPopup(record);
          }}
        />
      ) : null,
  });

  /* ── data fetching ── */
  useEffect(() => {
    getLeaveList();
  }, [userRoleId]);

  useEffect(() => {
    form.setFieldsValue({ month: moment().month() + 1 });
  }, []);

  /**
   * FIX: Replaced the old `new Promise(...)` wrapper (which called bare `reject()`
   * causing "Unknown promise rejection reason") with a plain async function that
   * uses try/catch/finally.  Call sites no longer need .catch() because this
   * function handles all errors internally and never throws.
   */
  const getLeaveList = async () => {
    const { status_id, start_date, duration_type, month, end_date } =
      form.getFieldsValue();
    const year = dateInDetail(getKenyanDateTime()).year;

    // Guard: form not yet initialised — exit silently instead of reject(undefined)
    if (status_id === undefined) return;

    setLeaveList([]);
    setLeaveStatusId(status_id);
    setDisplayTaskLoader(true);

    const payload = {
      status_id: status_id === "0" ? "" : status_id === "4" ? "4,5" : status_id,
      officesupervisorid: [""],
      selected_role_id: userRoleId,
      show_my_leave: true,
    };

    if (duration_type === 1 && start_date && end_date) {
      payload.start_date = getMySqlDate(start_date);
      payload.end_date = getMySqlDate(end_date);
    } else if (duration_type === 2 && typeof month === "number") {
      const { startDateOfMonth, lastDateOfMonth } = getMonthDateRange(year, month);
      payload.start_date = moment(startDateOfMonth).format("YYYY-MM-DD");
      payload.end_date = moment(lastDateOfMonth).format("YYYY-MM-DD");
    } else if (duration_type === 3) {
      payload.start_date = getMySqlDate(start_date);
      payload.end_date = weeks(7, getMySqlDate(start_date));
    } else if (duration_type === 4) {
      payload.start_date = momentDate(addMonthinDate(getinitialdate()));
      payload.end_date = momentDate(addMonthinDate(getKenyanDateTime(), 3));
    }

    try {
      const { code, leaves = [] } = await CALL_API(
        `leave-list/${ClientId}`,
        "post",
        payload
      );

      if (includes([STATUS_CODE.SUCCESS, STATUS_CODE.RECORD_NOT_FOUND], code)) {
        const filteredLeaves = leaves.filter(
          (item) => item.userid === userInfo.userid
        );
        setAllLeaveList(filteredLeaves);

        const leaveType = form.getFieldValue("leave_type");
        setLeaveList(
          leaveType
            ? filteredLeaves.filter((item) => item.leave_category_id === leaveType)
            : filteredLeaves
        );
      }
      // Non-success codes: silently ignore — list stays empty, no rejection thrown
    } catch (err) {
      // Network / API error — log but never rethrow so the browser overlay stays quiet
      console.error("getLeaveList error:", err);
    } finally {
      // Always clear the spinner regardless of outcome
      setDisplayTaskLoader(false);
    }
  };

  const validateDateRange = (date) => {
    const endDate = form.getFieldValue("end_date");
    if (endDate && moment(endDate).isBefore(date)) {
      form.setFieldsValue({ end_date: date });
    }
  };

  function weeks(days, date) {
    const newdate = new Date(date);
    newdate.setDate(newdate.getDate() + days);
    return newdate.toISOString().slice(0, 10);
  }

  const getEffectiveDate = (newDate, originalDate, format) => {
    const d = !newDate || newDate === "null" || newDate === "" ? originalDate : newDate;
    return moment(d).format(format);
  };

  const prepareCSV = () => {
    setCSVLoader(true);

    const currentStatusId = form.getFieldValue("status_id");

    const sourceData =
      currentStatusId === "4"
        ? leaveList.filter(
          (val) =>
            val?.status_id === "4" ||
            (val?.status_id === "5" && val?.having_cancel_request === "1")
        )
        : currentStatusId === "5"
          ? leaveList.filter(
            (val) =>
              val?.status_id === "5" &&
              (!val?.having_cancel_request ||
                val?.having_cancel_request === "0" ||
                val?.having_cancel_request === "")
          )
          : leaveList;

    if (sourceData.length === 0) {
      message.error("No data available to download.");
      setCSVLoader(false);
      return;
    }

    const rows = sourceData.reduce((result, leave) => {
      const baseRow = {
        "Leave Type": leave.leave_category_name,
        "Name": leave.fullname,
        "Start Date": getEffectiveDate(leave.new_start_date, leave.start_date, "Do MMM YYYY"),
        "Start Day": getEffectiveDate(leave.new_start_date, leave.start_date, "dddd"),
        "End Date": getEffectiveDate(leave.new_end_date, leave.end_date, "Do MMM YYYY"),
        "End Day": getEffectiveDate(leave.new_end_date, leave.end_date, "dddd"),
        "Days":
          !leave.new_number_of_days ||
            leave.new_number_of_days === "null" ||
            leave.new_number_of_days === ""
            ? leave.number_of_days
            : leave.new_number_of_days,
      };

      if (currentStatusId === "5") {
        result.push({
          ...baseRow,
          "Approved by": getApproverName(leave.approved_rejected_by),
          "Description": leave.reason,
        });
      } else if (currentStatusId === "6") {
        result.push({
          ...baseRow,
          "Rejected by": getApproverName(leave.approved_rejected_by),
          "Decline Reason": leave.decline_reason,
        });
      } else {
        result.push({ ...baseRow, "Description": leave.reason });
      }
      return result;
    }, []);

    downloadCSV(rows, currentStatusId);
    setCSVLoader(false);
  };

  const tableDataSource =
    leaveStatusId === "4" ? data1 :
      leaveStatusId === "5" ? data2 :
        leaveList;

  const approvalTabs = useMemo(
    () => [
      {
        id: "leave_approvals",
        label: "Leave",
        component: (
          <>
            <LeaveToolbar ref={containerRef}>
              <LeaveFilters>
                <Form
                  form={form}
                  name="approval_form"
                  layout="inline"
                  initialValues={{
                    duration_type: 4,
                    month: moment().month() + 1,
                    status_id: "0",
                    start_date: momentDate(addMonthinDate(getKenyanDateTime(), -1)),
                    end_date: momentDate(addDaysinDate(getKenyanDateTime())),
                    leave_type: undefined,
                  }}
                  onValuesChange={(changedValues) => {
                    // getLeaveList is now a plain async fn — no .catch() needed
                    if (changedValues.duration_type === 2) {
                      form.setFieldsValue({ month: moment().month() + 1 });
                      setTimeout(() => getLeaveList(), 0);
                    } else {
                      getLeaveList();
                    }
                  }}
                >
                  {/* Status */}
                  <Form.Item name="status_id" rules={[{ required: true, message: "" }]}>
                    <Select
                      placeholder="Select..."
                      fieldNames={{ label: "status_name", value: "status_id" }}
                      options={concat(
                        { status_id: "0", status_name: "All" },
                        orderBy(statusListFromID, [(val) => val.status_id], ["asc"])
                      )}
                      getPopupContainer={() => containerRef.current}
                    />
                  </Form.Item>

                  {/* Duration type */}
                  <Form.Item name="duration_type" rules={[{ required: true, message: "" }]}>
                    <Select
                      placeholder="Select..."
                      options={APPROVAL_FILTER_TYPES}
                      getPopupContainer={() => containerRef.current}
                    />
                  </Form.Item>

                  {/* Dynamic date / month picker */}
                  <Form.Item className="outer-form" shouldUpdate>
                    {({ getFieldValue }) => {
                      const dtype = getFieldValue("duration_type");
                      if (dtype === 1) return (
                        <Row>
                          <Col>
                            <Form.Item
                              name="start_date"
                              rules={[{
                                validator: (_, v) =>
                                  moment(v).isValid() ? Promise.resolve() : Promise.reject(new Error("Invalid date")),
                              }]}
                            >
                              <DatePicker
                                placeholder="Start date"
                                format="DD-MM-YYYY"
                                onChange={validateDateRange}
                                allowClear={false}
                                getPopupContainer={() => containerRef.current}
                              />
                            </Form.Item>
                          </Col>
                          <Col>
                            <Form.Item
                              name="end_date"
                              rules={[{
                                validator: (_, v) =>
                                  moment(v).isValid() ? Promise.resolve() : Promise.reject(new Error("Invalid date")),
                              }]}
                            >
                              <DatePicker
                                placeholder="End date"
                                format="DD-MM-YYYY"
                                onChange={validateDateRange}
                                allowClear={false}
                                getPopupContainer={() => containerRef.current}
                              />
                            </Form.Item>
                          </Col>
                        </Row>
                      );
                      if (dtype === 3) return (
                        <Form.Item
                          name="start_date"
                          rules={[{
                            validator: (_, v) =>
                              moment(v).isValid() ? Promise.resolve() : Promise.reject(new Error("Invalid date")),
                          }]}
                        >
                          <DatePicker
                            placeholder="Start date"
                            format="DD-MM-YYYY"
                            onChange={validateDateRange}
                            allowClear={false}
                            getPopupContainer={() => containerRef.current}
                          />
                        </Form.Item>
                      );
                      if (dtype === 2) return (
                        <Form.Item name="month" rules={[{ required: true, message: "" }]}>
                          <Select
                            placeholder="Select month"
                            options={MONTH_LIST.map((m) => ({ label: m.label, value: m.index }))}
                            getPopupContainer={() => containerRef.current}
                          />
                        </Form.Item>
                      );
                      return null;
                    }}
                  </Form.Item>

                  {/* Leave category */}
                  <Form.Item name="leave_type">
                    <Select
                      showSearch
                      allowClear
                      placeholder="All Categories"
                      optionFilterProp="label"
                      filterOption={(input, option) =>
                        option.label?.toLowerCase().includes(input.toLowerCase())
                      }
                      options={availableLeaveCategories}
                      fieldNames={{ label: "leave_category_name", value: "leave_category_id" }}
                      getPopupContainer={() => containerRef.current}
                    />
                  </Form.Item>
                </Form>
              </LeaveFilters>

              <div>
                <ExportBtn onClick={prepareCSV}>
                  {CSVloader ? "Generating…" : "Export CSV file"}
                </ExportBtn>
              </div>
            </LeaveToolbar>

            <Spin spinning={displayTaskLoader}>
              <StyledTable
                dataSource={tableDataSource}
                columns={columns}
                rowClassName={(_, index) => (index % 2 === 0 ? "" : "non-white")}
                rowKey="user_leave_id"
                // ── NEW: row click opens the View popup ──
                onRow={(record) => ({
                  onClick: () => openViewPopup(record),
                })}
                pagination={{
                  pageSize: 10,
                  showSizeChanger: false,
                  style: { padding: "12px 20px" },
                }}
              />
            </Spin>
          </>
        ),
      },
      {
        id: "time_approvals",
        label: "Time",
        component: (
          <TimeReport
            showCategories={showCategories}
            onShowCategoriesChange={setShowCategories}
          />
        ),
      },
      {
        id: "claims_approvals",
        label: "Claims",
        component: <ClaimsReport />,
      },
      {
        id: "fund_approvals",
        label: "Fund Request",
        component: (
          <FundReport
            markAsPaidRecord={markAsPaidRecord}
            onMarkAsPaidChange={setMarkAsPaidRecord}
            sendRecord={sendRecord}
            onSendRecordChange={setSendRecord}
          />
        ),
      },
    ],
    [
      leaveList, displayTaskLoader, leaveStatusId,
      showCategories, markAsPaidRecord, sendRecord,
      availableLeaveCategories, CSVloader, tableDataSource,
    ]
  );

  return (
    <PageWrapper>
      {!isSubPageOpen && <PageTitle>My History</PageTitle>}

      {!isSubPageOpen && (
        <TabsList
          items={approvalTabs}
          handleTabSelect={(id) => setTabId(id)}
          tabId={tabId}
        />
      )}

      {showCategories && (
        <TimeReport
          showCategories={showCategories}
          onShowCategoriesChange={setShowCategories}
        />
      )}

      {(markAsPaidRecord || sendRecord) && (
        <FundReport
          markAsPaidRecord={markAsPaidRecord}
          onMarkAsPaidChange={setMarkAsPaidRecord}
          sendRecord={sendRecord}
          onSendRecordChange={setSendRecord}
        />
      )}

      {/* ── NEW: edit popup — same LeaveForm used inside the calendar flow ── */}
      <LeaveForm
        isModalVisible={displayLeaveModal}
        closeModal={() => toggleLeaveModal(false)}
        leaveDetails={leaveDetails}
        getLeaveList={getLeaveList}
      />

      {/* ── NEW: view popup, opened on row click — matches the provided screenshot ── */}
      <Modal
        open={viewPopupVisible}
        onCancel={() => setViewPopupVisible(false)}
        footer={null}
        width={420}
        closable={false}
      >
        {viewPopupRecord && (
          <>
            <ViewPopupHeader>
              <StatusDot color={getStatusColor(viewPopupRecord.status_id)} />
              <ViewPopupName>{viewPopupRecord.fullname}</ViewPopupName>
              <ViewPopupIconGroup>
                {/* ── UPDATED: edit/amend icon now shows for pending AND approved
                    (not-already-amending) leaves, matching LeaveCalendar.js's amend flow ── */}
                {isCancelable(viewPopupRecord) && (
                  <EditOutlined
                    style={EDIT_ICON_STYLE}
                    onClick={() => {
                      setViewPopupVisible(false);
                      openEditPopup(viewPopupRecord);
                    }}
                  />
                )}
                <EditIconBtn onClick={() => setViewPopupVisible(false)}>
                  <CloseOutlined />
                </EditIconBtn>
              </ViewPopupIconGroup>
            </ViewPopupHeader>

            <ViewPopupRow>
              <ViewPopupLabel>From date:</ViewPopupLabel>
              <ViewPopupValue>
                {getEffectiveDate(viewPopupRecord.new_start_date, viewPopupRecord.start_date, "Do MMM YYYY")}
              </ViewPopupValue>
            </ViewPopupRow>

            <ViewPopupRow>
              <ViewPopupLabel>To date:</ViewPopupLabel>
              <ViewPopupValue>
                {getEffectiveDate(viewPopupRecord.new_end_date, viewPopupRecord.end_date, "Do MMM YYYY")}
              </ViewPopupValue>
            </ViewPopupRow>

            <ViewPopupRow>
              <ViewPopupLabel>No. of days:</ViewPopupLabel>
              <ViewPopupValue>{getDaysValue(viewPopupRecord)}</ViewPopupValue>
            </ViewPopupRow>

            <ViewPopupRow>
              <ViewPopupLabel>Status:</ViewPopupLabel>
              <ViewPopupValue>{getStatusLabel(viewPopupRecord.status_id)}</ViewPopupValue>
            </ViewPopupRow>

            <ViewPopupRow>
              <ViewPopupLabel>Leave Type:</ViewPopupLabel>
              <ViewPopupValue>{viewPopupRecord.leave_category_name}</ViewPopupValue>
            </ViewPopupRow>

            {/* ── NEW: Approved by row — resolves the email in approved_rejected_by
                against userList to show the approver's name ── */}
            {viewPopupRecord.status_id === "5" && (
              <ViewPopupRow>
                <ViewPopupLabel>Approved by:</ViewPopupLabel>
                <ViewPopupValue>
                  {getApproverName(viewPopupRecord.approved_rejected_by)}
                </ViewPopupValue>
              </ViewPopupRow>
            )}

            {/* ── UPDATED: Description content now sits beneath its label ── */}
            <ViewPopupRow style={{ flexDirection: "column" }}>
              <ViewPopupLabel style={{ marginBottom: 6 }}>Description:</ViewPopupLabel>
              <ViewPopupValue>{viewPopupRecord.reason}</ViewPopupValue>
            </ViewPopupRow>
            {viewPopupRecord.status_id === "6" && (
              <ViewPopupRow style={{ flexDirection: "column" }}>
                <ViewPopupLabel style={{ marginBottom: 6 }}>Decline Reason:</ViewPopupLabel>
                <ViewPopupValue>{viewPopupRecord.decline_reason || "-"}</ViewPopupValue>
              </ViewPopupRow>
            )}

            {/* ── FIXED: show either "Amend" (approved leaves, status 5) OR
                "Cancel Leave" (pending leaves, status 4) — never both together. ── */}
            {isCancelable(viewPopupRecord) && (
              <ViewPopupFooter>
                {viewPopupRecord.status_id === "5" ? (
                  <ButtonFilled
                    onClick={() => {
                      setViewPopupVisible(false);
                      openEditPopup(viewPopupRecord);
                    }}
                  >
                    Amend
                  </ButtonFilled>
                ) : (
                  <ButtonFilled onClick={() => handleCancelLeave(viewPopupRecord)}>
                    Cancel Leave
                  </ButtonFilled>
                )}
              </ViewPopupFooter>
            )}
          </>
        )}
      </Modal>

      {/* ── NEW: "Cancel Leave" confirmation — Yes (ButtonFilled) / No (ButtonOutlined) ── */}
      <Modal
        open={cancelConfirmOpen}
        onCancel={() => { setCancelConfirmOpen(false); setRecordPendingCancel(null); }}
        footer={null}
        width={420}
        closable={false}
        centered
      >
        <div style={{ textAlign: "left", padding: "8px 4px" }}>
          <p style={{ marginBottom: 28 }}>
            Are you sure you want to cancel your leave application?
          </p>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 16 }}>
            <ButtonOutlined
              onClick={() => { setCancelConfirmOpen(false); setRecordPendingCancel(null); }}
              disabled={cancelingLeave}
            >
              No
            </ButtonOutlined>
            <ButtonFilled onClick={confirmCancelLeave} disabled={cancelingLeave}>
              {cancelingLeave ? "Cancelling…" : "Yes"}
            </ButtonFilled>
          </div>
        </div>
      </Modal>
    </PageWrapper>
  );
}

const mapStateToProps = (state) => ({
  userRoleId: getUserRoleId(state),
  statusListFromID: getStatusListFromID(state, ["4", "5", "6"]),
  selectedDepartmentId: getSelectedDepartmentId(state),
  userInfo: getUserInfo(state),
  leaveCategoryList: getLeaveCategoryList(state),
  ClientId: getClientid(state),
  userList: getUserList(state),
  roleList: getRoleList(state),
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators({ updateProcessingModal: toggleProcessingModal }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps, null)(Myleavereport);